import { ReactNode } from 'react'
import { BoxConstraints, RenderBox } from '../rendering'
import { renderElement } from '../document'

/**
 * AlignmentRenderBox – implements alignment positioning logic similar to Flutter
 */
export class AlignmentRenderBox extends RenderBox<{
  alignment: { x: number; y: number }
  widthFactor?: number
  heightFactor?: number
}> {
  performLayout(parentConstraints: BoxConstraints): void {
    // Determine if we should shrink-wrap in each dimension
    const shrinkWrapWidth =
      this.props.widthFactor !== undefined || parentConstraints.maxWidth === Infinity
    const shrinkWrapHeight =
      this.props.heightFactor !== undefined || parentConstraints.maxHeight === Infinity

    // Size is determined by parent constraints and/or child size
    if (this.children.length > 0) {
      const child = this.children[0]

      // Layout child with loose constraints to let it determine its natural size
      child.performLayout(BoxConstraints.loosen(parentConstraints))

      // Determine our size based on child size, factors, and parent constraints
      let width: number
      let height: number

      if (shrinkWrapWidth) {
        width = child.box.width * (this.props.widthFactor ?? 1.0)
      } else {
        width = parentConstraints.maxWidth
      }

      if (shrinkWrapHeight) {
        height = child.box.height * (this.props.heightFactor ?? 1.0)
      } else {
        height = parentConstraints.maxHeight
      }

      // Constrain our size to parent constraints
      this.box.width = Math.min(
        Math.max(width, parentConstraints.minWidth),
        parentConstraints.maxWidth
      )
      this.box.height = Math.min(
        Math.max(height, parentConstraints.minHeight),
        parentConstraints.maxHeight
      )

      // Position the child according to alignment
      // Convert alignment from (-1 to 1) range to (0 to 1) for calculation
      const xFactor = (this.props.alignment.x + 1) / 2
      const yFactor = (this.props.alignment.y + 1) / 2

      // Calculate available space for positioning
      const extraWidth = this.box.width - child.box.width
      const extraHeight = this.box.height - child.box.height

      // Set child position based on alignment
      child.box.x = extraWidth * xFactor
      child.box.y = extraHeight * yFactor
    } else {
      // No child, use minimum size
      this.box.width = shrinkWrapWidth ? 0 : parentConstraints.maxWidth
      this.box.height = shrinkWrapHeight ? 0 : parentConstraints.maxHeight

      // Constrain to parent constraints
      this.box.width = Math.min(
        Math.max(this.box.width, parentConstraints.minWidth),
        parentConstraints.maxWidth
      )
      this.box.height = Math.min(
        Math.max(this.box.height, parentConstraints.minHeight),
        parentConstraints.maxHeight
      )
    }

    this.box.x = 0
    this.box.y = 0
  }
}

// Name string constants for alignment presets
export type Alignment =
  | 'topLeft'
  | 'topCenter'
  | 'topRight'
  | 'centerLeft'
  | 'center'
  | 'centerRight'
  | 'bottomLeft'
  | 'bottomCenter'
  | 'bottomRight'

// Type for alignment parameter
export type AlignmentValue = { x: number; y: number } | Alignment

// Named alignment presets, matching Flutter's Alignment constants
const alignmentPresets: Record<Alignment, { x: number; y: number }> = {
  topLeft: { x: -1, y: -1 },
  topCenter: { x: 0, y: -1 },
  topRight: { x: 1, y: -1 },
  centerLeft: { x: -1, y: 0 },
  center: { x: 0, y: 0 },
  centerRight: { x: 1, y: 0 },
  bottomLeft: { x: -1, y: 1 },
  bottomCenter: { x: 0, y: 1 },
  bottomRight: { x: 1, y: 1 }
}

// Helper function to convert named alignments to coordinates
function resolveAlignmentCoordinates(alignment: AlignmentValue): { x: number; y: number } {
  if (typeof alignment === 'object') {
    return alignment
  }
  return alignmentPresets[alignment]
}

// Align component matching Flutter's Align
export function Align({
  alignment,
  widthFactor,
  heightFactor,
  children
}: {
  alignment: AlignmentValue
  widthFactor?: number
  heightFactor?: number
  children: ReactNode
}) {
  const alignmentCoords = resolveAlignmentCoordinates(alignment)

  return renderElement(
    new AlignmentRenderBox({
      alignment: alignmentCoords,
      widthFactor,
      heightFactor
    }),
    children
  )
}

// Center component as a convenience wrapper over Align with center alignment
export function Center({
  widthFactor = 1,
  heightFactor = 1,
  children
}: {
  widthFactor?: number
  heightFactor?: number
  children: ReactNode
}) {
  return (
    <Align alignment="center" widthFactor={widthFactor} heightFactor={heightFactor}>
      {children}
    </Align>
  )
}
